import { navigateTo } from '@Actions/navigate.action';
import * as stationActions from '@Actions/stations.action';
import { error, success } from '@innovyze/stylovyze';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	selectSharedSensorGroups,
	selectSharedSensors,
} from '@Selectors/stylovyzeStore.selectors';
import {
	addImagesToLiftStationService,
	addSensorGroupsToLiftStationService,
	addSensorsToLiftStationService,
	removeImagesFromLiftStationService,
	removeSensorGroupsFromLiftStationService,
	removeSensorsFromLiftStationService,
	upsertLiftStationService,
} from '@Services/liftStations';
import { ns } from '@Translations/translations';
import { MAIN_ROUTES } from '@Types/routes';
import { LiftStation, LiftStationFormFields } from '@Types/stations.types';
import { upsertLiftStationMessages } from '@Utils/liftStations.constants';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { put, retry, select, takeLatest } from 'redux-saga/effects';

function* upsertLiftStationSaga(action: PayloadAction<LiftStationFormFields>) {
	const {
		upsertLiftStationResolved: createLiftStationResolved,
		upsertLiftStationRejected: createLiftStationRejected,
	} = stationActions;
	const isUpdate = !!action?.payload?._id;
	const mode = isUpdate ? 'update' : 'create';

	try {
		const stationResponse: AxiosResponse<LiftStation> = yield retry(
			1,
			1500,
			upsertLiftStationService,
			action.payload,
		);
		if (stationResponse.data) {
			if (stationResponse.data._id) {
				if (action.payload.sensors || action.payload.originalSensors) {
					const oldSensors = action.payload.originalSensors || [];
					const newSensors = action.payload.sensors || [];
					const oldSensorsSet = new Set(oldSensors);
					const newSensorsSet = new Set(newSensors);

					const sensorsToRemove = oldSensors.filter(
						sensor => !newSensorsSet.has(sensor),
					);
					const sensorsToAdd = newSensors.filter(
						sensor => !oldSensorsSet.has(sensor),
					);
					if (sensorsToAdd.length) {
						const storedSensors: ReturnType<
							typeof selectSharedSensors
						> = yield select(selectSharedSensors);

						yield retry(1, 1500, addSensorsToLiftStationService, {
							sensors: sensorsToAdd.map(sensorId => {
								const storedSensor = storedSensors?.find(
									s => s.sensorId === sensorId,
								);

								return {
									sensorId,
									sensorType: storedSensor?.sensorType,
								};
							}),
							id: stationResponse.data._id,
						});
					}
					if (sensorsToRemove.length) {
						yield retry(
							1,
							1500,
							removeSensorsFromLiftStationService,
							{
								sensors: sensorsToRemove,
								id: stationResponse.data._id,
							},
						);
					}
				}
				if (
					action.payload.sensorGroups ||
					action.payload.originalSensorGroups
				) {
					const oldSensorGroups =
						action.payload.originalSensorGroups || [];
					const newSensorGroups = action.payload.sensorGroups || [];
					const oldSensorGroupsSet = new Set(oldSensorGroups);
					const newSensorGroupsSet = new Set(newSensorGroups);
					const sensorGroupsToRemove = oldSensorGroups.filter(
						sensorGroup => !newSensorGroupsSet.has(sensorGroup),
					);
					const sensorGroupsToAdd = newSensorGroups.filter(
						sensorGroup => !oldSensorGroupsSet.has(sensorGroup),
					);
					if (sensorGroupsToAdd.length) {
						const storedSensorGroups: ReturnType<
							typeof selectSharedSensorGroups
						> = yield select(selectSharedSensorGroups);

						yield retry(
							1,
							1500,
							addSensorGroupsToLiftStationService,
							{
								sensorGroups: sensorGroupsToAdd.map(
									sensorGroupId => {
										const storedSensorGroup =
											storedSensorGroups?.find(
												s => s._id === sensorGroupId,
											);

										return {
											sensorGroupId,
											sensorType:
												storedSensorGroup?.sensorType,
										};
									},
								),
								id: stationResponse.data._id,
							},
						);
					}
					if (sensorGroupsToRemove.length) {
						yield retry(
							1,
							1500,
							removeSensorGroupsFromLiftStationService,
							{
								sensorGroups: sensorGroupsToRemove,
								id: stationResponse.data._id,
							},
						);
					}
				}
				const { images } = action.payload;
				if (images) {
					if (images.length) {
						yield retry(
							1,
							1500,
							addImagesToLiftStationService,
							stationResponse.data._id,
							images,
						);
					} else {
						yield retry(
							1,
							1500,
							removeImagesFromLiftStationService,
							stationResponse.data._id,
						);
					}
				}
				yield put(
					success(
						upsertLiftStationMessages(t, ns)[
							`liftStation/${mode}/success`
						],
					),
				);
				yield put(navigateTo(MAIN_ROUTES.Stations));
				yield put(createLiftStationResolved());
			} else {
				yield put(createLiftStationRejected());
				yield put(
					error(
						// eslint-disable-next-line prettier/prettier
						upsertLiftStationMessages(t, ns)[`liftStation/${mode}/duplicate`],
					),
				);
			}
		} else {
			yield put(createLiftStationRejected());
			yield put(
				error(
					// eslint-disable-next-line prettier/prettier
					upsertLiftStationMessages(t, ns)[`liftStation/${mode}/error`],
				),
			);
		}
	} catch (e) {
		yield put(createLiftStationRejected());
		yield put(
			error(
				// eslint-disable-next-line prettier/prettier
				upsertLiftStationMessages(t, ns)[`liftStation/${mode}/error`],
			),
		);
	}
}

function* watchUpsertLiftStation() {
	yield takeLatest(stationActions.upsertLiftStation, upsertLiftStationSaga);
}

export default [watchUpsertLiftStation()];
